<template><div><h1 id="contributing" tabindex="-1"><a class="header-anchor" href="#contributing"><span>Contributing</span></a></h1>
<p>Contributions to MrChromebox firmware, scripts, and documentation are welcome! Whether you're fixing a typo, reporting a bug, or proposing a new feature, your help is appreciated.</p>
<h2 id="documentation-contributions" tabindex="-1"><a class="header-anchor" href="#documentation-contributions"><span>Documentation Contributions</span></a></h2>
<h3 id="website-documentation" tabindex="-1"><a class="header-anchor" href="#website-documentation"><span>Website Documentation</span></a></h3>
<p>Found a typo, outdated information, or want to improve the docs?</p>
<ul>
<li><strong>Report issues</strong>: <a href="https://github.com/MrChromebox/website/issues/" target="_blank" rel="noopener noreferrer">Website Issue Tracker</a></li>
<li><strong>Submit changes</strong>: Fork the repository and submit a pull request</li>
<li><strong>What to contribute</strong>:
<ul>
<li>Typo fixes and grammar improvements</li>
<li>Clarifications of confusing sections</li>
<li>Additional troubleshooting information</li>
<li>Updated screenshots or diagrams</li>
<li>New FAQ entries based on common questions</li>
</ul>
</li>
</ul>
<h3 id="documentation-guidelines" tabindex="-1"><a class="header-anchor" href="#documentation-guidelines"><span>Documentation Guidelines</span></a></h3>
<p>When contributing to documentation:</p>
<ul>
<li>Use clear, concise language</li>
<li>Test any commands or procedures before documenting</li>
<li>Include board names (HWID), not manufacturer model names</li>
<li>Link to related documentation where appropriate</li>
<li>Follow the existing formatting and structure</li>
</ul>
<h2 id="firmware-contributions" tabindex="-1"><a class="header-anchor" href="#firmware-contributions"><span>Firmware Contributions</span></a></h2>
<h3 id="reporting-firmware-bugs" tabindex="-1"><a class="header-anchor" href="#reporting-firmware-bugs"><span>Reporting Firmware Bugs</span></a></h3>
<p><strong>Before reporting</strong>, check:</p>
<ol>
<li>Issue isn't already reported: <a href="https://github.com/MrChromebox/firmware/issues/" target="_blank" rel="noopener noreferrer">Firmware Issue Tracker</a></li>
<li>You're using the latest firmware version</li>
<li>Issue is firmware-related (not OS/driver issue)</li>
</ol>
<p><strong>When reporting</strong>, include:</p>
<ul>
<li>Device board name (HWID)</li>
<li>Firmware version</li>
<li>Steps to reproduce</li>
<li>Expected vs actual behavior</li>
<li>Relevant logs (cbmem, dmesg)</li>
</ul>
<p>See <RouteLink to="/docs/support/debugging.html">Debugging Guide</RouteLink> for log collection instructions.</p>
<h3 id="firmware-development" tabindex="-1"><a class="header-anchor" href="#firmware-development"><span>Firmware Development</span></a></h3>
<p>Firmware improvements and bug fixes are welcome! The MrChromebox firmware is based on coreboot.</p>
<ul>
<li><strong>Firmware source</strong>: <a href="https://github.com/MrChromebox/coreboot" target="_blank" rel="noopener noreferrer">MrChromebox coreboot</a></li>
<li><strong>edk2 payload</strong>: <a href="https://github.com/MrChromebox/edk2" target="_blank" rel="noopener noreferrer">MrChromebox edk2</a></li>
<li><strong>Requirements</strong>:
<ul>
<li>Understanding of coreboot development</li>
<li>Ability to test changes on actual hardware</li>
<li>Familiarity with ChromeOS device architecture</li>
</ul>
</li>
</ul>
<p><strong>Pull request guidelines</strong>:</p>
<ul>
<li>Test thoroughly on affected hardware</li>
<li>Follow coreboot coding standards</li>
<li>Include clear commit messages</li>
<li>Document any user-facing changes</li>
</ul>
<h2 id="script-contributions" tabindex="-1"><a class="header-anchor" href="#script-contributions"><span>Script Contributions</span></a></h2>
<h3 id="reporting-script-issues" tabindex="-1"><a class="header-anchor" href="#reporting-script-issues"><span>Reporting Script Issues</span></a></h3>
<p>The Firmware Utility Script simplifies firmware installation and management.</p>
<ul>
<li><strong>Script issues</strong>: <a href="https://github.com/MrChromebox/scripts/issues/" target="_blank" rel="noopener noreferrer">Scripts Issue Tracker</a></li>
<li><strong>Script source</strong>: <a href="https://github.com/MrChromebox/scripts" target="_blank" rel="noopener noreferrer">MrChromebox Scripts</a></li>
</ul>
<p><strong>When reporting script bugs</strong>:</p>
<ul>
<li>Exact command run</li>
<li>Full output/error messages</li>
<li>Device board name</li>
<li>OS and ChromeOS version (if applicable)</li>
</ul>
<h3 id="script-development" tabindex="-1"><a class="header-anchor" href="#script-development"><span>Script Development</span></a></h3>
<p>Contributions to the script are welcome:</p>
<ul>
<li>Bug fixes for existing features</li>
<li>Improved error handling</li>
<li>Better user feedback</li>
<li>Platform-specific fixes</li>
</ul>
<p><strong>Guidelines</strong>:</p>
<ul>
<li>Maintain backward compatibility where possible</li>
<li>Test on both ChromeOS and Linux environments</li>
<li>Consider impact on all supported devices</li>
<li>Keep code readable and well-commented</li>
</ul>
<h2 id="device-support-requests" tabindex="-1"><a class="header-anchor" href="#device-support-requests"><span>Device Support Requests</span></a></h2>
<h3 id="adding-new-device-support" tabindex="-1"><a class="header-anchor" href="#adding-new-device-support"><span>Adding New Device Support</span></a></h3>
<p>To request support for a new device:</p>
<ol>
<li>Verify it's x86_64 (Intel/AMD) - ARM devices not supported</li>
<li>Check if already supported: <RouteLink to="/docs/supported-devices.html">Supported Devices</RouteLink></li>
<li>Open issue on <a href="https://github.com/MrChromebox/firmware/issues/" target="_blank" rel="noopener noreferrer">Firmware Issue Tracker</a></li>
<li>Include:
<ul>
<li>Device board name (HWID)</li>
<li>Device manufacturer and model</li>
<li>Hardware specifications (CPU, RAM, storage)</li>
<li>Any unique hardware features</li>
</ul>
</li>
</ol>
<p><strong>Note</strong>: Adding device support often requires:</p>
<ul>
<li>Physical hardware for testing</li>
<li>Community interest (multiple users requesting)</li>
<li>Hardware donations or loans help speed up the process</li>
</ul>
<h2 id="community-support" tabindex="-1"><a class="header-anchor" href="#community-support"><span>Community Support</span></a></h2>
<h3 id="helping-other-users" tabindex="-1"><a class="header-anchor" href="#helping-other-users"><span>Helping Other Users</span></a></h3>
<p>One of the best ways to contribute is helping other users:</p>
<ul>
<li>Answer questions on <a href="https://forum.chrultrabook.com/" target="_blank" rel="noopener noreferrer">Chrultrabook Forums</a></li>
<li>Share your experiences and solutions</li>
<li>Write blog posts or tutorials</li>
<li>Create video guides (with proper attribution)</li>
</ul>
<h3 id="spreading-the-word" tabindex="-1"><a class="header-anchor" href="#spreading-the-word"><span>Spreading the Word</span></a></h3>
<ul>
<li>Share the project with others who might benefit</li>
<li>Link to documentation when helping users</li>
<li>Credit MrChromebox when discussing the firmware</li>
</ul>
<h2 id="code-of-conduct" tabindex="-1"><a class="header-anchor" href="#code-of-conduct"><span>Code of Conduct</span></a></h2>
<p>When contributing, please:</p>
<ul>
<li>Be respectful and constructive</li>
<li>Focus on technical merit</li>
<li>Help newcomers learn</li>
<li>Give credit where due</li>
<li>Follow project guidelines</li>
</ul>
<h2 id="questions" tabindex="-1"><a class="header-anchor" href="#questions"><span>Questions?</span></a></h2>
<p>Not sure how to contribute or where to start?</p>
<ul>
<li>General questions: <a href="https://forum.chrultrabook.com/" target="_blank" rel="noopener noreferrer">Chrultrabook Forums</a></li>
<li>Development questions: Open an issue on the relevant GitHub repository</li>
<li>Direct contact: <a href="mailto:MrChromebox@gmail.com" target="_blank" rel="noopener noreferrer">Email MrChromebox</a> (for significant contributions or questions)</li>
</ul>
<h2 id="license" tabindex="-1"><a class="header-anchor" href="#license"><span>License</span></a></h2>
<p>By contributing, you agree that your contributions will be licensed under the same license as the project you're contributing to:</p>
<ul>
<li>Firmware: GPL-2.0</li>
<li>Scripts: GPL-3.0</li>
<li>Documentation: CC-BY-SA-4.0 (or as specified in repository)</li>
</ul>
<hr>
<p>Thank you for your interest in contributing to the MrChromebox project!</p>
</div></template>
<script>
export const _pageData = JSON.parse("{\"path\":\"/docs/contributing.html\",\"title\":\"Contributing\",\"lang\":\"en-US\",\"frontmatter\":{},\"git\":{\"updatedTime\":1764108479000,\"contributors\":[{\"name\":\"Matt DeVillier\",\"username\":\"\",\"email\":\"matt.devillier@gmail.com\",\"commits\":2}],\"changelog\":[{\"hash\":\"fa568da252bfada23ab1ba517ff5eec2c11aa2dd\",\"time\":1764108479000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"docs: fix coreboot capitalization consistency\"},{\"hash\":\"e3ec26d1248dc64d485aa16feb1e482f5bd78253\",\"time\":1764107460000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"docs: add contributing guide page\"}]},\"filePathRelative\":\"docs/contributing.md\"}")
export default { name: "contributing.md" }
</script>